import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { makeAuthenticatedApiCall } from '../apiHelper';
import type {
  CtvoAnalyzeV1ApiResponse,
  CtvoAnalyzeV1Result,
  CtvoStatutGlobal,
} from '../types/ctvoAnalyzeV1';

/**
 * Hook d'appel du moteur d'analyse CTVO v1.
 *
 * À utiliser quand le flag `form_cgia` est ON. Le hook ne fait rien
 * tant que `enabled === false` ou que `imageUrls.length === 0`.
 *
 * Stratégie de déclenchement : on relance l'analyse à chaque changement du
 * set d'images (clé = URLs jointes triées) — pour ne pas servir un verdict
 * périmé quand l'agence ajoute / retire une pièce. Le backend gère le cache
 * et le retry.
 *
 * Deux modes :
 *  - `registrationId` numérique → POST /api/cg/:id/analyze-v1 (post-soumission
 *    ou mode édition / review GOD)
 *  - `registrationId` null → POST /api/cg/analyze-v1/preflight (avant
 *    création de la CG, identifié par images_hash). À la soumission, le
 *    backend adopte le résultat préfligh pour ne pas re-payer un appel Claude.
 */

interface UseCtvoAnalyzeV1Args {
  registrationId: number | null;
  imageUrls: string[];
  enabled: boolean;
}

interface UseCtvoAnalyzeV1Result {
  result: CtvoAnalyzeV1Result | null;
  statut: CtvoStatutGlobal | 'en_cours' | null;
  isLoading: boolean;
  error: string | null;
}

const runAnalysis = async (
  registrationId: number | null,
  imageUrls: string[],
): Promise<CtvoAnalyzeV1ApiResponse> => {
  const url =
    registrationId !== null
      ? `/api/cg/${registrationId}/analyze-v1`
      : `/api/cg/analyze-v1/preflight`;
  const response = await makeAuthenticatedApiCall<CtvoAnalyzeV1ApiResponse>('post', url, {
    imageUrls,
  });
  return response.data;
};

export const useCtvoAnalyzeV1 = ({
  registrationId,
  imageUrls,
  enabled,
}: UseCtvoAnalyzeV1Args): UseCtvoAnalyzeV1Result => {
  const [result, setResult] = useState<CtvoAnalyzeV1Result | null>(null);
  const [statut, setStatut] = useState<CtvoStatutGlobal | 'en_cours' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const triggeredKeyRef = useRef<string | null>(null);

  const mutation = useMutation({
    mutationFn: ({ regId, urls }: { regId: number | null; urls: string[]; key: string }) =>
      runAnalysis(regId, urls),
    // Garde anti-course : chaque mutation embarque la clé du set qui l'a
    // déclenchée. Si le set a changé pendant l'appel (ex : pièce co-titulaire
    // ajoutée → nouvelle analyse partie), la réponse périmée est ignorée au
    // lieu d'écraser le verdict du set courant.
    onSuccess: (data, { key }) => {
      if (key !== triggeredKeyRef.current) return;
      if (data.success && data.data?.result_json) {
        setResult(data.data.result_json);
        setStatut(data.data.statut);
        setError(null);
      } else {
        setError(data.error ?? 'Analyse v1 sans données.');
        setStatut('analyse_echouee');
      }
    },
    onError: (err: unknown, { key }) => {
      if (key !== triggeredKeyRef.current) return;
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
      setStatut('analyse_echouee');
    },
  });

  // Déclenchement contrôlé : on lance l'analyse une fois par "set d'images"
  // (clé = URLs jointes + registrationId pour distinguer preflight vs
  // post-soumission). Si l'agence retire/ajoute une pièce ou si la CG est
  // créée entre-temps, la clé change et on relance proprement. Reset des
  // states pour éviter d'afficher un verdict périmé pendant l'attente.
  useEffect(() => {
    if (!enabled || imageUrls.length === 0) {
      // Réinitialise si le contexte se retire (flag off, plus d'images)
      if (triggeredKeyRef.current !== null) {
        triggeredKeyRef.current = null;
        setResult(null);
        setStatut(null);
        setError(null);
      }
      return;
    }

    // La clé inclut registrationId pour basculer proprement preflight → post
    // au moment de la création de la CG (le backend adopte le résultat,
    // donc on n'aura pas de re-coût Claude — mais on re-fetch le row final).
    const key = `${registrationId ?? 'preflight'}::${[...imageUrls].sort().join('\n')}`;
    if (triggeredKeyRef.current === key) return; // déjà déclenchée pour ce set

    triggeredKeyRef.current = key;
    setResult(null);
    setStatut('en_cours');
    setError(null);
    mutation.mutate({ regId: registrationId, urls: imageUrls, key });
  }, [enabled, registrationId, imageUrls, mutation]);

  return {
    result,
    statut,
    isLoading: mutation.isPending,
    error,
  };
};
